/**
 * Public Library page — lists videos with their attached PDF studies.
 * Anyone can view; no auth required. Counts are incremented server-side
 * via the file streaming endpoints. Supports `?v=<videoId>` deep links
 * which scroll to and highlight the requested video (used by the
 * per-video "Share" button).
 */
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, FileDown, Eye, Film, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { libraryApi } from "../lib/adminApi";

export default function LibraryPage() {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchParams] = useSearchParams();
    const targetId = searchParams.get("v");

    useEffect(() => {
        (async () => {
            try {
                const list = await libraryApi.listVideos();
                setVideos(list);
            } catch (e) {
                setError(e?.response?.data?.detail || "Could not load library.");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div
            className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-14 py-10 relative z-10 space-y-8"
            data-testid="library-page"
        >
            <header className="codex-card px-6 py-6">
                <p className="font-mono-os text-[10px] tracking-[0.36em] uppercase text-[#D4AF37]">
                    Codex Library
                </p>
                <h1 className="mt-2 font-serif-display text-3xl sm:text-4xl text-[#F9E596]">
                    Videos · Studies · Witness
                </h1>
                <p className="mt-3 text-[#CBD5E1] max-w-xl">
                    Curated teaching videos paired with downloadable PDF studies. All
                    studies are sealed-OS outputs — read, mark, and carry them as is.
                </p>
            </header>

            {error && (
                <p className="text-[#F43F5E] text-sm" data-testid="library-error">
                    {error}
                </p>
            )}
            {loading ? (
                <p className="text-[#CBD5E1] text-sm flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Loading the shelf…
                </p>
            ) : videos.length === 0 ? (
                <div className="codex-card px-6 py-12 text-center" data-testid="library-empty">
                    <Film size={28} className="mx-auto text-[#D4AF37]/70" />
                    <p className="mt-3 font-serif-display text-xl text-[#F9E596]">
                        The shelf is empty.
                    </p>
                    <p className="mt-2 text-[#CBD5E1] text-sm">
                        The first videos and studies will appear here once they're posted.
                    </p>
                </div>
            ) : (
                <div className="space-y-6">
                    {videos.map((v) => (
                        <VideoCard
                            key={v.id}
                            video={v}
                            highlighted={targetId === v.id}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function VideoCard({ video, highlighted }) {
    const cardRef = useRef(null);
    const [copied, setCopied] = useState(false);

    // When this card is the deep-link target, scroll it into view + apply a
    // brief gold ring so the visitor knows what was shared.
    useEffect(() => {
        if (highlighted && cardRef.current) {
            cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [highlighted]);

    async function handleShare() {
        const url = `${window.location.origin}/library?v=${encodeURIComponent(video.id)}`;
        try {
            if (navigator.share) {
                try {
                    await navigator.share({
                        title: video.title,
                        text: `${video.title} — from the Codex Library`,
                        url,
                    });
                    return;
                } catch (_e) {
                    // user cancelled or share unsupported — fall through to clipboard
                }
            }
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Share link copied.");
            setTimeout(() => setCopied(false), 2200);
        } catch (_e) {
            toast.error("Could not copy the link.");
        }
    }

    return (
        <article
            ref={cardRef}
            data-testid={`library-video-${video.id}`}
            className={`codex-card p-5 sm:p-6 space-y-4 transition-shadow ${
                highlighted
                    ? "ring-2 ring-[#F9E596]/70 shadow-[0_0_42px_-6px_rgba(249,229,150,0.55)]"
                    : ""
            }`}
        >
            <div className="grid md:grid-cols-[1fr_1.4fr] gap-5">
                <div className="bg-black/60 rounded-md overflow-hidden aspect-video flex items-center justify-center">
                    <video
                        controls
                        preload="metadata"
                        playsInline
                        className="w-full h-full"
                        src={libraryApi.streamUrl(video.id)}
                    >
                        Your browser does not support video playback.
                    </video>
                </div>
                <div className="space-y-2 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                        <h3 className="font-serif-display text-2xl text-[#F9E596] leading-tight">
                            {video.title}
                        </h3>
                        <button
                            data-testid={`library-share-${video.id}`}
                            onClick={handleShare}
                            className="codex-icon-button !w-9 !h-9 flex-shrink-0"
                            aria-label="Share this video"
                            title="Copy share link"
                        >
                            {copied ? (
                                <Check size={15} strokeWidth={1.8} />
                            ) : (
                                <Share2 size={15} strokeWidth={1.6} />
                            )}
                        </button>
                    </div>
                    {video.description && (
                        <p className="text-[#CBD5E1] text-sm leading-relaxed">
                            {video.description}
                        </p>
                    )}
                    <p className="font-mono-os text-[10px] tracking-[0.28em] uppercase text-[#94A3B8] flex items-center gap-2 pt-1">
                        <Eye size={11} /> {video.view_count} views
                    </p>
                    {video.studies?.length > 0 && (
                        <div className="pt-3">
                            <p className="font-mono-os text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-2">
                                Attached studies
                            </p>
                            <ul className="space-y-2">
                                {video.studies.map((s) => (
                                    <li
                                        key={s.id}
                                        className="flex items-center justify-between gap-3 bg-[#0A1220]/50 border border-[#D4AF37]/25 rounded-md px-3 py-2"
                                    >
                                        <span className="text-sm text-[#F1F5F9] truncate">
                                            {s.title}
                                        </span>
                                        <a
                                            data-testid={`library-download-${s.id}`}
                                            href={libraryApi.studyUrl(s.id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="codex-button !px-3 !py-2 !text-[10px]"
                                        >
                                            <FileDown size={12} /> PDF
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
}
